
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

interface CostSummary {
  totalItems: number;
  totalCost: number | undefined;
  averageUnitCost: number | undefined;
  method: string;
  updateShopify: boolean;
}

interface CostDetail {
  sku: string;
  variant?: string;
  variantImage?: string;
  receivedQuantity: number | undefined;
  totalCost: number | undefined;
  unitCost: number | undefined;
  currentCost: number | undefined;
  finalCost: number | undefined;
  costDifference: number | undefined;
}

interface CalculationExample {
  sku: string;
  currentQty: number;
  currentCost: number;
  receivedQty: number;
  receivedCost: number;
  result: number;
}

@Component({
  name: 'CostSummaryDialog'
})
export default class CostSummaryDialog extends Vue {
  @Prop({ type: Boolean, default: false })
  value!: boolean;

  @Prop({ type: Object, required: true })
  summary!: CostSummary;

  @Prop({ type: Array, required: true })
  costDetails!: CostDetail[];

  @Prop({ type: Object, default: () => ({ enabled: false, successful: [], failed: [] }) })
  shopifyUpdateResult!: any;

  // Data properties
  private downloading = false;

  // Computed properties
  get dialog(): boolean {
    return this.value;
  }

  set dialog(val: boolean) {
    this.$emit('input', val);
  }

  get detailHeaders() {
    return [
      { text: 'Product', value: 'product', sortable: false, width: '200px' },
      { text: 'Qty', value: 'receivedQuantity', align: 'center', sortable: false, width: '80px' },
      { text: 'Total Cost', value: 'totalCost', align: 'center', sortable: false, width: '100px' },
      { text: 'Unit Cost', value: 'unitCost', align: 'center', sortable: false, width: '90px' },
      { text: 'Current', value: 'currentCost', align: 'center', sortable: false, width: '90px' },
      { text: 'Final Cost', value: 'finalCost', align: 'center', sortable: false, width: '110px' },
      { text: 'Difference', value: 'costDifference', align: 'center', sortable: false, width: '100px' }
    ];
  }

  get shopifyUpdateStatus() {
    if (!this.summary.updateShopify) {
      return {
        color: 'grey',
        icon: 'mdi-cloud-off',
        title: 'Shopify Update Skipped',
        description: 'Cost updates were not sent to Shopify per your selection.',
        status: 'Skipped'
      };
    }

    if (this.shopifyUpdateResult.enabled === false) {
      return {
        color: 'warning',
        icon: 'mdi-cloud-alert',
        title: 'Shopify Update Not Performed',
        description: 'Cost tracking was enabled but Shopify updates were skipped.',
        status: 'Not Updated'
      };
    }

    const successful = this.shopifyUpdateResult.successful?.length || 0;
    const failed = this.shopifyUpdateResult.failed?.length || 0;
    const total = successful + failed;

    if (failed === 0) {
      return {
        color: 'success',
        icon: 'mdi-cloud-check',
        title: 'All Costs Updated Successfully',
        description: `Successfully updated ${successful} product cost${successful !== 1 ? 's' : ''} in Shopify.`,
        status: 'Success'
      };
    } else if (successful > 0) {
      return {
        color: 'warning',
        icon: 'mdi-cloud-alert',
        title: 'Partial Update Success',
        description: `Updated ${successful} of ${total} costs. ${failed} update${failed !== 1 ? 's' : ''} failed.`,
        status: 'Partial'
      };
    } else {
      return {
        color: 'error',
        icon: 'mdi-cloud-off',
        title: 'Cost Update Failed',
        description: `Failed to update ${failed} product cost${failed !== 1 ? 's' : ''} in Shopify.`,
        status: 'Failed'
      };
    }
  }

  get showFormula(): boolean {
    return this.summary.method === 'average' && this.costDetails.some(item => (item.currentCost || 0) > 0);
  }

  get calculationExamples(): CalculationExample[] {
    return this.costDetails
      .filter(item => (item.currentCost || 0) > 0 && (item.finalCost || 0) !== (item.unitCost || 0))
      .slice(0, 3)
      .map(item => ({
        sku: item.sku,
        currentQty: 10, // This would come from actual data
        currentCost: item.currentCost || 0,
        receivedQty: item.receivedQuantity || 0,
        receivedCost: item.unitCost || 0,
        result: item.finalCost || 0
      }));
  }

  // Methods
  private formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return '0.0000';
    }
    return Number(value).toFixed(4);
  }

  private formatCostDifference(diff: number | undefined | null): string {
    if (diff === null || diff === undefined || isNaN(Number(diff))) {
      return '$0.0000';
    }
    const numDiff = Number(diff);
    const abs = Math.abs(numDiff);
    const sign = numDiff >= 0 ? '+' : '-';
    return `${sign}$${this.formatCurrency(abs)}`;
  }

  private getCostDifferenceColor(diff: number | undefined | null): string {
    if (diff === null || diff === undefined || isNaN(Number(diff))) return 'grey';
    const numDiff = Number(diff);
    if (Math.abs(numDiff) < 0.01) return 'grey';
    return numDiff > 0 ? 'success' : 'error';
  }

  private getCostDifferenceIcon(diff: number | undefined | null): string {
    if (diff === null || diff === undefined || isNaN(Number(diff))) return 'mdi-equal';
    const numDiff = Number(diff);
    if (Math.abs(numDiff) < 0.01) return 'mdi-equal';
    return numDiff > 0 ? 'mdi-trending-up' : 'mdi-trending-down';
  }

  private close(): void {
    this.dialog = false;
  }

  private async downloadSummary(): Promise<void> {
    this.downloading = true;
    try {
      // Create CSV content
      const csvContent = this.generateCSVContent();
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost-summary-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      this.$emit('success', 'Cost summary downloaded successfully');
    } catch (error) {
      this.$emit('error', 'Failed to download cost summary');
    } finally {
      this.downloading = false;
    }
  }

  private generateCSVContent(): string {
    const headers = [
      'SKU',
      'Variant',
      'Received Quantity',
      'Total Cost',
      'Unit Cost',
      'Current Cost',
      'Final Cost',
      'Cost Difference',
      'Calculation Method'
    ];

    const rows = this.costDetails.map(item => [
      item.sku || '',
      item.variant || '',
      (item.receivedQuantity || 0).toString(),
      this.formatCurrency(item.totalCost),
      this.formatCurrency(item.unitCost),
      this.formatCurrency(item.currentCost),
      this.formatCurrency(item.finalCost),
      this.formatCurrency(item.costDifference),
      this.summary.method || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csvContent;
  }
}
